// Loan Compliance Module v1.0 PR 9 — Track 3 Annual Legal Compliance
// Review entry point on the Pool/Trust Detail page. Renders the pool's
// review history by audit year (status, findings counts, signed PLCR
// link, Officer's Certificate Support link) and a deep link into the
// workbench. Privilege-aware: A3 / E4 see full status and can open the
// workbench; B3 / B4 / B5 see the findings summary only.

import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useSpvPoolLegalReviews } from "@/hooks/compliance/useLegalReview";
import { isFullPoolReview } from "@/api/compliance/legalReviewTypes";
import type {
  LoanLegalReviewStatus,
  PoolLegalReviewDto,
  PoolLegalReviewSummaryDto,
} from "@/api/compliance/legalReviewTypes";
import type { SpvPoolDto } from "@/types/spvPool";
import { useDateFormatter } from "@/utils/datetime";

interface PoolLegalReviewTabProps {
  pool: SpvPoolDto;
}

const STATUS_BADGE: Record<
  LoanLegalReviewStatus,
  React.ComponentProps<typeof Badge>["status"]
> = {
  Scoping: "draft",
  Fieldwork: "info",
  Draft: "draft",
  InReview: "review",
  Finalized: "finalized",
  Reissued: "archived",
};

export function LegalReviewTab({ pool }: PoolLegalReviewTabProps) {
  const auth = useAuth();
  const isPrivileged =
    auth.hasRoleCode("A3") ||
    auth.hasRoleCode("E4") ||
    auth.hasRoleCode("ADMIN");

  const reviewsQuery = useSpvPoolLegalReviews(pool.id);

  if (reviewsQuery.isLoading) {
    return (
      <Card>
        <p className="text-small text-gray-mid italic m-0">
          Loading legal review history…
        </p>
      </Card>
    );
  }

  if (reviewsQuery.isError) {
    return (
      <Card>
        <p className="text-small text-status-error m-0" role="alert">
          Could not load legal review history. Refresh to retry.
        </p>
      </Card>
    );
  }

  const reviews = (reviewsQuery.data ?? [])
    .slice()
    .sort((a, b) => b.auditYear - a.auditYear);

  return (
    <div
      className="flex flex-col gap-space-3"
      data-testid="pool-legal-review-tab"
    >
      <Card>
        <div className="flex items-center justify-between gap-space-3">
          <div>
            <h2 className="font-display font-bold text-h3 m-0">Legal Review</h2>
            <p className="text-caption text-gray-mid mt-space-1 m-0">
              Track 3 Annual Pool-Level Legal Compliance Review (PLCR) history.
              {!isPrivileged && (
                <>
                  {" "}
                  Working notes, scoping memos, and memos are restricted to
                  attorney (A3) and compliance oversight (E4) roles.
                </>
              )}
            </p>
          </div>
          {isPrivileged && (
            <Link
              to={`/compliance/legal-review?scope=pool&poolId=${pool.id}`}
              data-testid="pool-legal-review-tab-open-workspace"
            >
              <Button variant="secondary" size="sm">
                Open workspace
              </Button>
            </Link>
          )}
        </div>
      </Card>

      {reviews.length === 0 ? (
        <Card data-testid="pool-legal-review-empty">
          <p className="text-small text-gray-mid italic m-0">
            No legal review engagements have been opened for this pool yet.
          </p>
        </Card>
      ) : (
        <ul
          aria-label="Legal review history"
          className="divide-y divide-border-light bg-white-pure border border-border-light rounded-lg"
        >
          {reviews.map((r) => (
            <ReviewRow
              key={r.id}
              review={r}
              poolId={pool.id}
              isPrivileged={isPrivileged}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ReviewRow({
  review,
  poolId,
  isPrivileged,
}: {
  review: PoolLegalReviewDto | PoolLegalReviewSummaryDto;
  poolId: string;
  isPrivileged: boolean;
}) {
  const findingsCount = isFullPoolReview(review)
    ? review.modules.filter((m) => m.conclusion === "ExceptionRaised").length
    : review.exceptionRaisedCount;
  const { date } = useDateFormatter();

  return (
    <li
      className="px-space-3 py-space-3 flex items-center gap-space-3"
      data-testid={`pool-legal-review-row-${review.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-space-2">
          <span className="font-display font-semibold text-small">
            Audit year {review.auditYear}
          </span>
          <Badge status={STATUS_BADGE[review.status]}>{review.status}</Badge>
          {review.signedAt && (
            <span className="text-caption text-gray-mid">
              Signed {date(review.signedAt)}
            </span>
          )}
        </div>
        <div className="mt-space-1 text-caption text-gray-mid">
          {findingsCount > 0 ? (
            <span data-testid={`pool-legal-review-findings-${review.id}`}>
              {findingsCount} finding{findingsCount === 1 ? "" : "s"}
            </span>
          ) : (
            <span data-testid={`pool-legal-review-findings-${review.id}`}>
              No findings
            </span>
          )}
          {review.finalPlcrReportSubmissionId && (
            <>
              {" "}
              ·{" "}
              <Link
                to={`/reports/${review.finalPlcrReportSubmissionId}`}
                className="text-gold-shadow hover:underline"
                data-testid={`pool-legal-review-plcr-${review.id}`}
              >
                Signed PLCR
              </Link>
            </>
          )}
          {review.officerCertSupportReportSubmissionId && (
            <>
              {" "}
              ·{" "}
              <Link
                to={`/reports/${review.officerCertSupportReportSubmissionId}`}
                className="text-gold-shadow hover:underline"
                data-testid={`pool-legal-review-officer-cert-${review.id}`}
              >
                Officer&apos;s Cert Support
              </Link>
            </>
          )}
          {!isPrivileged && (
            <span
              className="ml-space-2 italic"
              data-testid={`pool-legal-review-summary-only-${review.id}`}
            >
              · summary view (privileged content restricted)
            </span>
          )}
        </div>
      </div>
      {isPrivileged && (
        <Link
          to={`/compliance/legal-review/${review.id}?scope=pool&poolId=${poolId}`}
          data-testid={`pool-legal-review-open-${review.id}`}
        >
          <Button variant="tertiary" size="sm">
            Open
          </Button>
        </Link>
      )}
    </li>
  );
}
